import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

import { ARTICLE_BODY_ORIGIN } from "@/grading/constants";
import PowerWordButton from "@/view/PowerWordButton";
import { useDynamicDataProvider } from "@/view/TiptapArticleBody/extensions/DataProvider";
import { ComponentProps } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";

interface PowerWordOptions {
  componentOrigin: keyof typeof ARTICLE_BODY_ORIGIN;
}

const Component = ({
  node,
  extension,
  editor,
}: ComponentProps<PowerWordOptions>) => {
  const { componentOrigin } = extension.options;
  const { currentFontSize: fontSize, userProperties } =
    useDynamicDataProvider(editor);

  const powerWordText = node.textContent;
  const { definition, id } = node.attrs;
  const { canViewPowerWords, isLiteUser, isStudent } = userProperties;
  const isLiteNonStudent = isLiteUser && !isStudent;
  const isArticleBodyFromViewPage =
    componentOrigin === ARTICLE_BODY_ORIGIN.VIEW_PAGE;

  // We want to ensure lite users see inline power word buttons
  // since when they are clicked they will be provided with some
  // information about how they work to help them understand that
  // this is a feature they could have access to with premium.
  if (isArticleBodyFromViewPage && (canViewPowerWords || isLiteNonStudent)) {
    return (
      <NodeViewWrapper as="span">
        <PowerWordButton
          currentFontSize={fontSize}
          isLiteNonStudent={isLiteNonStudent}
          powerWord={powerWordText}
          powerWordId={id}
          wordDefinition={definition}
        >
          {powerWordText}
        </PowerWordButton>
      </NodeViewWrapper>
    );
  } else {
    return (
      <NodeViewWrapper as="span">
        <span>{powerWordText}</span>
      </NodeViewWrapper>
    );
  }
};

export const PowerWord = Node.create<PowerWordOptions>({
  name: "powerWord_block",
  content: "text*",
  group: "inline",
  inline: true,
  addAttributes() {
    return {
      id: {},
      definition: { default: null },
    };
  },
  addOptions() {
    return {
      componentOrigin:
        ARTICLE_BODY_ORIGIN.VIEW_PAGE as keyof typeof ARTICLE_BODY_ORIGIN,
    };
  },
  renderHTML() {
    return ["span", 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});
